import { useState } from 'react';
import { ArrowDown, ArrowUp, Check, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { Button } from '@kop/ui';
import { createSafetyBoardRepository, safetyBoardFailureMessage } from './repository';
import {
  boardCategoryColors,
  boardCategoryIcons,
  type BoardCategoryColor,
  type BoardCategoryIcon,
  type SafetyBoardSettings,
} from './model';

type CategoryDraft = {
  id: string;
  label: string;
  color: BoardCategoryColor;
  icon: BoardCategoryIcon;
};

const colorLabel: Readonly<Record<BoardCategoryColor, string>> = {
  navy: '남색',
  blue: '파랑',
  green: '초록',
  amber: '주황',
  red: '빨강',
  slate: '회색',
};
const iconLabel: Readonly<Record<BoardCategoryIcon, string>> = {
  megaphone: '공지',
  shield: '안전',
  alert: '경고',
  book: '자료',
  bell: '알림',
  info: '안내',
};

export function SafetyBoardPolicyDialog({
  initialSettings,
  onClose,
  onSaved,
}: {
  initialSettings: SafetyBoardSettings;
  onClose(): void;
  onSaved(settings: SafetyBoardSettings): void;
}) {
  const [settings, setSettings] = useState(initialSettings);
  const [categories, setCategories] = useState<CategoryDraft[]>(() =>
    initialSettings.categories.map(({ id, label, color, icon }) => ({ id, label, color, icon })),
  );
  const [maxImages, setMaxImages] = useState(initialSettings.maxImages);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const apply = (value: SafetyBoardSettings) => {
    setSettings(value);
    setCategories(
      value.categories.map(({ id, label, color, icon }) => ({ id, label, color, icon })),
    );
    setMaxImages(value.maxImages);
    onSaved(value);
  };

  const load = async () => {
    setBusy(true);
    setError('');
    try {
      apply(await (await createSafetyBoardRepository(settings.siteId)).settingsForAdministration());
    } catch (caught) {
      setError(safetyBoardFailureMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setCategories(next);
    setSaved(false);
  };

  const save = async () => {
    if (categories.length === 0 || categories.some((item) => !item.label.trim())) {
      setError('분류 이름을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const repository = await createSafetyBoardRepository(settings.siteId);
      await repository.updatePolicy({
        expectedVersion: settings.configuration.version,
        categories: categories.map((item, order) => ({
          ...item,
          label: item.label.trim(),
          order,
          active: true,
        })),
        maxImages,
      });
      apply(await repository.settingsForAdministration());
      setSaved(true);
    } catch (caught) {
      setError(safetyBoardFailureMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="safety-board-policy-layer" role="presentation" onMouseDown={onClose}>
      <section
        className="safety-board-policy glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-board-policy-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <h3 id="safety-board-policy-title">게시판 설정</h3>
          <div>
            <Button
              variant="quiet"
              size="compact"
              icon={<RefreshCw size={16} />}
              aria-label="새로고침"
              title="새로고침"
              disabled={busy}
              onClick={() => void load()}
            />
            <Button
              variant="quiet"
              size="compact"
              icon={<X size={17} />}
              aria-label="닫기"
              onClick={onClose}
            />
          </div>
        </header>

        {error && (
          <p className="safety-board-policy__error" role="alert">
            {error}
          </p>
        )}

        <div className="safety-board-policy__categories">
          {categories.map((item, index) => (
            <div className="safety-board-policy__row" key={item.id}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <input
                maxLength={80}
                value={item.label}
                aria-label={`${index + 1}번 분류`}
                onChange={(event) => {
                  setCategories((current) =>
                    current.map((entry, entryIndex) =>
                      entryIndex === index ? { ...entry, label: event.target.value } : entry,
                    ),
                  );
                  setSaved(false);
                }}
              />
              <select
                value={item.color}
                aria-label={`${index + 1}번 분류 색상`}
                onChange={(event) => {
                  const color = event.target.value as BoardCategoryColor;
                  setCategories((current) =>
                    current.map((entry, entryIndex) =>
                      entryIndex === index ? { ...entry, color } : entry,
                    ),
                  );
                  setSaved(false);
                }}
              >
                {boardCategoryColors.map((color) => (
                  <option key={color} value={color}>
                    {colorLabel[color]}
                  </option>
                ))}
              </select>
              <select
                value={item.icon}
                aria-label={`${index + 1}번 분류 아이콘`}
                onChange={(event) => {
                  const icon = event.target.value as BoardCategoryIcon;
                  setCategories((current) =>
                    current.map((entry, entryIndex) =>
                      entryIndex === index ? { ...entry, icon } : entry,
                    ),
                  );
                  setSaved(false);
                }}
              >
                {boardCategoryIcons.map((icon) => (
                  <option key={icon} value={icon}>
                    {iconLabel[icon]}
                  </option>
                ))}
              </select>
              <Button
                variant="quiet"
                size="compact"
                icon={<ArrowUp size={15} />}
                aria-label={`${index + 1}번 분류 위로`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              />
              <Button
                variant="quiet"
                size="compact"
                icon={<ArrowDown size={15} />}
                aria-label={`${index + 1}번 분류 아래로`}
                disabled={index === categories.length - 1}
                onClick={() => move(index, 1)}
              />
              <Button
                variant="quiet"
                size="compact"
                icon={<Trash2 size={15} />}
                aria-label={`${index + 1}번 분류 삭제`}
                disabled={categories.length === 1}
                onClick={() => {
                  setCategories((current) =>
                    current.filter((_, entryIndex) => entryIndex !== index),
                  );
                  setSaved(false);
                }}
              />
            </div>
          ))}
          <Button
            variant="quiet"
            size="compact"
            className="safety-board-policy__add"
            icon={<Plus size={15} />}
            onClick={() => {
              setCategories((current) => [
                ...current,
                {
                  id: `board-${crypto.randomUUID()}`,
                  label: '',
                  color: 'navy',
                  icon: 'megaphone',
                },
              ]);
              setSaved(false);
            }}
          >
            분류 추가
          </Button>
        </div>

        <label className="safety-board-policy__images">
          <span>이미지 제한</span>
          <select
            value={maxImages}
            onChange={(event) => {
              setMaxImages(Number(event.target.value));
              setSaved(false);
            }}
          >
            {Array.from({ length: 7 }, (_, value) => (
              <option key={value} value={value}>
                {value}장
              </option>
            ))}
          </select>
        </label>

        <footer>
          {saved && <Check size={18} aria-label="저장됨" />}
          <Button
            variant="primary"
            size="compact"
            icon={<Save size={16} />}
            disabled={busy || !settings.authority.canManage}
            onClick={() => void save()}
          >
            저장
          </Button>
        </footer>
      </section>
    </div>
  );
}
